import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { selectFlow } from '../store/app';
import { pagesRoutes, type RoutesProps } from './pagesConfig';
import { useCookieBanner, useSendEvents } from '../hooks';
import { EVENTS as E } from '../constants';
import { MainLayout } from '@applyft-web/ui-components';
import { LegalFooter } from '../../components/LegalFooter';
import { parseOnboardingPath } from '../../locales/languageConfig';
import * as S from './styled';

interface RouteWithAppContainerProps extends RoutesProps {}

export const RouteWithAppContainer = ({
  path,
  element,
  withoutWrap,
  withButton = true,
  wrapperProps = {},
}: RouteWithAppContainerProps) => {
  const pb = withButton ? 88 : 16;
  const sendEvents = useSendEvents();
  const flow = useSelector(selectFlow);
  const initEventSentRef = useRef(false);

  useEffect(() => {
    if (initEventSentRef.current || flow?.indexOf(path.replace('/', '')) !== 0) return;
    initEventSentRef.current = true;
    sendEvents(E.ONBOARDING_START);
  }, [sendEvents, flow, path]);

  useCookieBanner(path);

  const content = withoutWrap ? (
    element
  ) : (
    <MainLayout pb={pb} {...wrapperProps}>
      {element}
    </MainLayout>
  );

  if (path === '/*') {
    return content;
  }

  return (
    <S.Page>
      <S.Main>{content}</S.Main>
      <LegalFooter screenId={element.props.screenId ?? path.slice(1)} />
    </S.Page>
  );
};

export const Router = () => {
  const location = useLocation();
  const { pathname, search } = location;
  const navigate = useNavigate();
  const cleanPath = parseOnboardingPath(pathname).cleanPath;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const handleBackButton = (event: any) => {
      event.preventDefault();
      navigate(`${pathname}${search}`, { replace: true });
    };

    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [pathname, search, navigate]);

  const wrappedRoutes = pagesRoutes.map((route) => ({
    ...route,
    element: <RouteWithAppContainer {...route} />,
  }));

  return useRoutes(wrappedRoutes, {
    ...location,
    pathname: cleanPath,
  });
};
